"use client";

import { Component } from "react";
import { ApiClient } from "../api-client";
import { createT, detectLanguage } from "../i18n";
import type { PrimeStyleTryonProps } from "./types";
import { getApiKey, getApiUrl } from "./utils/locale";
import { ErrorView } from "./views/ErrorView";
import { PrimeStyleTryonInner } from "./PrimeStyleTryonInner";

interface BoundaryState {
  hasError: boolean;
  resetKey: number;
  recoverToManual: boolean;
}

class PrimeStyleTryonErrorBoundary extends Component<PrimeStyleTryonProps, BoundaryState> {
  state: BoundaryState = { hasError: false, resetKey: 0, recoverToManual: false };

  static getDerivedStateFromError(): Partial<BoundaryState> {
    return { hasError: true, recoverToManual: false };
  }

  componentDidCatch(error: unknown): void {
    console.error("[ps-sdk] PrimeStyleTryon render failed:", error);
    const message = error instanceof Error ? error.message : "PrimeStyleTryon render failed";
    void new ApiClient(getApiKey(), getApiUrl(this.props.apiUrl))
      .reportClientError({
        message,
        code: "RENDER_ERROR",
        stack: error instanceof Error ? error.stack : undefined,
        component: "PrimeStyleTryon",
        productId: this.props.productId || this.props.productImage,
        productTitle: this.props.productTitle,
        productUrl: this.props.productUrl,
        severity: "high",
      })
      .catch(() => {});
    this.props.onError?.({
      message,
      code: "RENDER_ERROR",
    });
  }

  private handleManualMeasurements = (): void => {
    this.setState((state) => ({
      hasError: false,
      resetKey: state.resetKey + 1,
      recoverToManual: true,
    }));
  };

  render() {
    if (this.state.hasError) {
      const t = createT(this.props.locale || detectLanguage());
      return (
        <ErrorView
          productImage={this.props.productImage}
          productTitle={this.props.productTitle}
          onManualMeasurements={this.handleManualMeasurements}
          cn={this.props.classNames ?? {}}
          t={t}
        />
      );
    }

    return (
      <PrimeStyleTryonInner
        key={this.state.resetKey}
        {...this.props}
        initialView={this.state.recoverToManual ? "body-profile" : undefined}
        initialBodyProfileStep={this.state.recoverToManual ? "basics" : undefined}
      />
    );
  }
}

export function PrimeStyleTryon(props: PrimeStyleTryonProps) {
  return <PrimeStyleTryonErrorBoundary {...props} />;
}
